import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { GoldIcon, WoodIcon, RumIcon } from '@/components/game/GameIcons'

/**
 * Shared premium primitives for the Corsalia game surfaces — parchment-gold
 * cards on a deep-ocean tint, the gilded primary button (rich gradient, light
 * sweep + lift on hover, pressed inset on :active), and a thin progress bar.
 */

export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 ${className}`}>
      {children}
    </div>
  )
}

/** Gold call-to-action. `group` + inner sweep span give the hover shine. */
export function GoldButton({
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={
        'group relative overflow-hidden rounded-lg font-display font-semibold text-ocean-900 ' +
        'bg-gradient-to-b from-[#E7D199] to-sand-500 ring-1 ring-sand-100/50 ' +
        'shadow-[inset_0_1px_0_rgba(245,237,215,.5),0_4px_10px_-6px_rgba(0,0,0,.55)] ' +
        'transition-[transform,box-shadow,filter] duration-200 ease-out ' +
        'hover:-translate-y-0.5 hover:from-[#FBEFC8] hover:to-[#E3BE72] ' +
        'hover:shadow-[0_0_0_1px_rgba(245,237,215,.7),0_14px_28px_-10px_rgba(229,204,143,.8)] ' +
        'active:translate-y-0 active:scale-[.985] active:shadow-[inset_0_2px_6px_rgba(90,42,18,.45)] ' +
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none ' +
        className
      }
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/60 to-transparent
                   -translate-x-[180%] transition-transform duration-700 ease-out group-hover:translate-x-[420%]"
      />
      <span className="relative">{children}</span>
    </button>
  )
}

/**
 * Hover tooltip bubble. Drop inside a trigger that carries `group/tip relative`;
 * the bubble fades in above the trigger on hover. Pure CSS — no JS, no portal.
 */
export function HintBubble({
  label,
  className = '',
  placement = 'top',
  align = 'center',
}: {
  label: ReactNode
  className?: string
  /** 'top' opens the bubble above the target (default); 'bottom' opens below —
   *  use 'bottom' when the target sits inside an `overflow-hidden` container
   *  that would clip an upward bubble. */
  placement?: 'top' | 'bottom'
  /** Horizontal anchor. 'center' (default) centers on the target; 'start' pins
   *  the bubble's left edge to the target (opens rightward) and 'end' pins its
   *  right edge — use these for the first/last item of an icon row so the bubble
   *  never spills off the viewport edge on mobile. */
  align?: 'center' | 'start' | 'end'
}) {
  const pos = placement === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
  const horiz = align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2'
  return (
    <span
      role="tooltip"
      className={
        `pointer-events-none absolute ${pos} ${horiz} z-30 w-44 max-w-[calc(100vw-1.5rem)] ` +
        'rounded-lg border border-sand-500/25 bg-ocean-900/95 px-2.5 py-1.5 ' +
        'text-xs font-normal normal-case leading-snug whitespace-normal text-ocean-100 text-left ' +
        'opacity-0 shadow-xl transition-opacity duration-150 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100 ' +
        className
      }
    >
      {label}
    </span>
  )
}

/**
 * Compact metric card — an icon chip over a value and an uppercase label.
 * Used in the captain "registre du bord", the public profile and leaderboards.
 */
export function StatTile({ icon, value, label }: { icon: ReactNode; value: ReactNode; label: string }) {
  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-4 flex flex-col gap-2">
      <span className="grid place-items-center w-10 h-10 rounded-lg bg-ocean-900/50 border border-sand-500/25">{icon}</span>
      <p className="font-display text-xl text-sand-100 truncate" title={typeof value === 'string' ? value : undefined}>{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-ocean-300">{label}</p>
    </div>
  )
}

/** Small pill — gold by default, `rum` for warnings/admin, `ghost` for muted. */
export function Badge({ children, tone = 'gold' }: { children: ReactNode; tone?: 'gold' | 'rum' | 'ghost' }) {
  const styles = {
    gold: 'bg-sand-300/15 border-sand-300/40 text-sand-200',
    rum: 'bg-rum-700/30 border-rum-500/40 text-sand-200',
    ghost: 'bg-ocean-900/40 border-sand-500/15 text-ocean-300',
  }[tone]
  return <span className={`text-xs px-2.5 py-1 rounded-full border ${styles}`}>{children}</span>
}

export function ProgressBar({ pct, danger = false }: { pct: number; danger?: boolean }) {
  return (
    <div className="h-1.5 bg-ocean-900 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-1000 ${danger ? 'bg-rum-500' : 'bg-ocean-300'}`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  )
}

/**
 * Inline "gold · wood · rhum" readout with the resource icons instead of the
 * words. Only the resources passed are shown (and in that order). Use everywhere
 * a mission spells out amounts — reports, raid/commerce prep — for consistency.
 */
export function ResourceTally({
  gold,
  wood,
  rhum,
  className = '',
  iconClassName = 'w-3.5 h-3.5 text-sand-300',
}: {
  gold?: number
  wood?: number
  rhum?: number
  className?: string
  iconClassName?: string
}) {
  const fmt = (n: number) => n.toLocaleString('fr-FR')
  return (
    <span className={`inline-flex items-center gap-2 tabular-nums ${className}`}>
      {gold !== undefined && (
        <span className="inline-flex items-center gap-1"><GoldIcon className={iconClassName} />{fmt(gold)}</span>
      )}
      {wood !== undefined && (
        <span className="inline-flex items-center gap-1"><WoodIcon className={iconClassName} />{fmt(wood)}</span>
      )}
      {rhum !== undefined && (
        <span className="inline-flex items-center gap-1"><RumIcon className={iconClassName} />{fmt(rhum)}</span>
      )}
    </span>
  )
}
